// src/com/bulk_products/BulkProducts.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
// import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import LinkIcon from '@mui/icons-material/Link'; // 업체와 상품 연결
import LinkOffIcon from '@mui/icons-material/LinkOff'; // 업체와 상품 연결 해제
import DoneIcon from '@mui/icons-material/Done';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord, TableColumnType } from '../../value/AppConst';
import {
    authTableTreeRepo, bulkCompanyProductsAllListRepo, bulkProductsListRepo
} from '../../value/AppModel';
import {
    LIST_BULK_PRODUCTS, SET_USAGE_BULK_PRODUCTS,
    ADD_BULK_PRODUCTS, EDIT_BULK_PRODUCTS, REMOVE_BULK_PRODUCTS,
    productListReflectCompanyRelation
} from './BulkProductsGql';
import { BulkProductsDictionary } from './BulkProductsDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import {
    LIST_ALL_BULK_COMPANY_PRODUCTS, getCompaniesUsingProduct,
    ADD_BULK_COMPANY_PRODUCTS, REMOVE_BULK_COMPANY_PRODUCTS
} from '../bulk_company_products/BulkCompanyProductsGql';
import PromptDialog from '../../common/PromptDialog';
import TableDialog from '../../common/TableDialog';

const CompanyTableProps = {
    tableId: 'companyNamesUsingProductTable',
    uniqueKey: 'comName',
    flexOption: {
        multiChecker:false,
        hideFirstTopButton: true,
        hideColumnConfig: true,
        hideBubble: true,
        growToFill: true,
    },
    columns: [
        {
            id: 'comName', label: '업체명', width: 280,
            show: true,
            option:{align: 'inherit', type:TableColumnType.TEXT}
        },
    ]
};

/**
 * (BULK_PRODUCTS) 정보 View Controller
 * Bulk Products
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-05-14 14:51:47 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function BulkProducts({
    companySelected, // {bulkCompanyId, comName}
}) {
    // const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const company = useMemo(()=>companySelected ? companySelected : {}, [companySelected]);
    const bulkProductsListData = useReactiveVar(bulkProductsListRepo);
    const bulkCompanyProductsListData = useReactiveVar(bulkCompanyProductsAllListRepo);
    const products = useMemo(()=>{
        return productListReflectCompanyRelation(
            bulkProductsListData.records,
            bulkCompanyProductsListData.records,
            company.bulkCompanyId,
            company.comName
        );
    }, [bulkProductsListData, bulkCompanyProductsListData, company]);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const [tableDialogData, setTableDialogData] = useState({open: false, param: {}});
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getBulkProductsList, responseList] = useLazyQuery(LIST_BULK_PRODUCTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: BulkProductsDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getBulkCompanyProductsList, responseBulkCompanyProductsList] = useLazyQuery(LIST_ALL_BULK_COMPANY_PRODUCTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetBulkCompanyProductsList(data, option)},
        onError: (error) => addAlert({title: '벌크상품 목록 조회 오류', message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at con-sole (Uncaught exception) ###
    const [addBulkProducts, responseAdd] = useMutation( ADD_BULK_PRODUCTS, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: BulkProductsDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editBulkProducts, responseEdit] = useMutation( EDIT_BULK_PRODUCTS, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: BulkProductsDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeBulkProducts, responseRemove] = useMutation( REMOVE_BULK_PRODUCTS, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: BulkProductsDictionary.errorTitle.Remove, message: [error.message]})
	});
	const [setUsageBulkProducts, responseSetUsage] = useMutation(SET_USAGE_BULK_PRODUCTS, {
		onCompleted: (data, option) => onCompletesetUsage(data, option),
		onError: (error) => addAlert({title: BulkProductsDictionary.errorTitle.SetUsage, message: [error.message]})
	});
    const [linkBulkCompanyProducts, responseLinkBulkCompanyProducts] = useMutation(ADD_BULK_COMPANY_PRODUCTS, {
        onCompleted: (data, option) => onCompleteLink(data, option),
        onError: (error) => addAlert({title: '벌크상품-업체 연결 오류', message: [error.message]})
    });
    const [unlinkBulkCompanyProducts, responseUnlinkBulkCompanyProducts] = useMutation(REMOVE_BULK_COMPANY_PRODUCTS, {
        onCompleted: (data, option) => onCompleteUnlink(data, option),
        onError: (error) => addAlert({title: '벌크상품-업체 연결 해제 오류', message: [error.message]})
    })

    const getBulkProductsListNow = () => {
        getBulkProductsList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        getBulkCompanyProductsList();
        const diff = new Date().getTime()/1000 - bulkProductsListData.when;
        if(diff > 300) //  || bulkProductsListData.records.length === 0 - Don't use this condition
            getBulkProductsListNow();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.bulkProductsList) {
            bulkProductsListRepo({when:new Date().getTime()/1000, records:data.bulkProductsList});
        }
    };

    const onCompleteGetBulkCompanyProductsList = (data, clientOption) => {
        if(data.bulkCompanyProductsListAll) {
            bulkCompanyProductsAllListRepo({when:new Date().getTime()/1000, records:data.bulkCompanyProductsListAll});
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.bulkProductsAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getBulkProductsListNow();
        }
        else addAlert({title:BulkProductsDictionary.errorTitle.Add, message:[data.bulkProductsAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.bulkProductsEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getBulkProductsListNow();
        }
        else addAlert({title:BulkProductsDictionary.errorTitle.Edit, message:[data.bulkProductsEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.bulkProductsRemove.ok) getBulkProductsListNow();
        else addAlert({title:BulkProductsDictionary.errorTitle.Remove, message:[data.bulkProductsRemove.message]});
    };

	const onCompletesetUsage = (data, option) => {
        if(data.bulkProductsSetUsage.ok) getBulkProductsListNow();
        else addAlert({title:BulkProductsDictionary.errorTitle.SetUsage, message:[data.bulkProductsSetUsage.message]});
    };

    const onCompleteLink = (data, option) => {
        if(data.bulkCompanyProductsAdd.ok) {
            getBulkCompanyProductsList();
        }
    };

    const onCompleteUnlink = (data, option) => {
        if(data.bulkCompanyProductsRemove.ok) {
            getBulkCompanyProductsList();
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getBulkProductsListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{bulkProducts:item}};
        if(isEdit) editBulkProducts(mutateParam);
        else addBulkProducts(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        setEditorState({open:true, item:item});
    };
    
    // PromptDialog onSubmit callback
    const onSubmitProdName = (newName, isEdit) => {
        setEditorState(AppObject.EmptyViewerState);

        if(editorState.item) {
            // Edit
            const data = {
                bulkProductsId: editorState.item.bulkProductsId,
                prodName: newName,
            };
            onRequestSave(data, true);
        }
        else onRequestSave({prodName: newName}, false);
    };

    const getCompanyMsgUsingProduct = (item) => {
        const list = getCompaniesUsingProduct(item.bulkProductsId, bulkCompanyProductsListData.records);
        if(list.length > 0) return '해당 상품과 연결된 업체는 ' + list.join(', ') + '입니다.';
        else return '해당 상품과 연결된 업체가 없습니다.';
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "상품정보 삭제",
            content: [
                `${item.prodName} 상품정보를 삭제하시겠습니까?`,
                getCompanyMsgUsingProduct(item),
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeBulkProducts({variables:{bulkProducts:{bulkProductsId:item.bulkProductsId}}});
    };

    const confirmLink = async (item) => {
        const confirm = await openConfirmDialog({
            title: "상품-업체 연결",
            content: [
                `${item.prodName} 상품과 ${company.comName} 업체를 연결하시겠습니까?`,
            ]
        });

        if(confirm) {
            linkBulkCompanyProducts({variables:{bulkCompanyProducts:{bulkCompanyId:company.bulkCompanyId, bulkProductsId:item.bulkProductsId}}});
        }
    };

    const confirmUnlink = async (item) => {
        const confirm = await openConfirmDialog({
            title: "상품-업체 연결 해제",
            content: [
                `${item.prodName} 상품과 ${company.comName} 업체의 연결을 해제하시겠습니까?`,
            ]
        });

        if(confirm) {
            unlinkBulkCompanyProducts({variables:{bulkCompanyProducts:{bulkCompanyId:company.bulkCompanyId, bulkProductsId:item.bulkProductsId}}});
        }
    };

    const showCompaniesUsingProduct = (item) => {
        const list = getCompaniesUsingProduct(item.bulkProductsId, bulkCompanyProductsListData.records);
        if(list.length > 0) {
            setTableDialogData({
                open: true,
                param: {
                    tableName: CompanyTableProps.tableId,
                    title: `${item.prodName} 상품과 연결된 업체`,
                    records: list.map((comName)=> ({comName: comName})),
                    tableProps: CompanyTableProps
                }
            });
        }
        else {
            addAlert({title:'연결업체', message:['해당 상품과 연결된 업체가 없습니다.']});
            // enqueueSnackbar('해당 상품과 연결된 업체가 없습니다.', {variant:'info', autoHideDuration:2000});
        }
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onRequestSetUsage = (item, useYn) => {
        const mutateParam = {variables:{bulkProducts:{bulkProductsId:item.bulkProductsId, useYn:useYn}}};
        setUsageBulkProducts(mutateParam);
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "상품정보 사용중지",
            content: [
                `${item.prodName} 상품정보를 사용중지하시겠습니까?`,
                getCompanyMsgUsingProduct(item)
            ]
        });

        if(confirm) onRequestSetUsage(item, 'N');
    };

    const confirmReusing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "상품정보 사용",
            content: [
                `${item.prodName} 상품정보를 사용하도록 변경하시겠습니까?`,
            ]
        });
        if(confirm) onRequestSetUsage(item, 'Y');
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            case AppObject.command.REUSE: confirmReusing(row); break;
            case AppObject.command.ON: confirmLink(row); break;
            case AppObject.command.OFF: confirmUnlink(row); break;
            case AppObject.command.TARIFF: showCompaniesUsingProduct(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_bulk_products_add);
    const canEdit = checkAuth(authTableTree.tree.pr_bulk_products_edit);
	const canSetUsage = checkAuth(authTableTree.tree.pr_bulk_products_set_usage);
    const canLink = checkAuth(authTableTree.tree.pr_bulk_company_products_add);
    const canUnlink = checkAuth(authTableTree.tree.pr_bulk_company_products_remove);

    const tools = canEdit ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>, title: '편집'},
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning, title: '삭제'},
        {id: AppObject.command.TARIFF, icon: <BusinessIcon/>, title: '연결업체', color: AppStyle.mui.info}
    ] : [];

    if(canSetUsage) {
        tools.push({
            id: AppObject.command.STOP_USING, icon: <StopCircleIcon/>,
            color: AppStyle.mui.error, title: '사용중지',
            where: (row)=>row.useYn === 'Y'
        });
        tools.push({
            id: AppObject.command.REUSE, icon: <PlayCircleFilledWhiteIcon/>,
            color: AppStyle.mui.success, title: '사용',
            where: (row)=>row.useYn === 'N'
        });
    }

    if(company.bulkCompanyId && canLink) {
        tools.push({
            id: AppObject.command.ON, icon: <LinkIcon/>, title: '업체와 연결',
            color: AppStyle.mui.success,
            where: (row)=>!row.owned
        });
    }

    if(company.bulkCompanyId && canUnlink) {
        tools.push({
            id: AppObject.command.OFF, icon: <LinkOffIcon/>, title: '업체 연결 해제',
            color: AppStyle.mui.warning,
            where: (row)=>row.owned
        });
    }

    const loadingAny
        = responseList.loading
        || responseAdd.loading
        || responseEdit.loading
        || responseRemove.loading
		|| responseSetUsage.loading
        || responseBulkCompanyProductsList.loading
        || responseLinkBulkCompanyProducts.loading
        || responseUnlinkBulkCompanyProducts.loading
    ;

    return(
        <ContentConainer>
            <ContentHeader>
                <Box
                    style={{
                        display: 'flex', alignItems:'center', justifyContent:'center',
                        textAlign: 'center', minWidth: 200,
                        paddingLeft: 10, paddingRight: 10, marginRight: 20,
                        backgroundColor: AppStyle.PrimaryRGB, color: 'white', borderRadius: 5,
                        height: '100%'
                    }}
                >
                    {
                        company.comName ?
                        <DoneIcon style={{marginRight:5}} /> :
                        <ArrowBackIcon style={{marginRight:5}} />
                    }
                    {
                        company.comName ?
                        company.comName : '업체를 선택하세요'
                    }
                </Box>
                <ControlBox>
                    {
                        products.length > 15 &&
                        <SearchBox
                            label="Bulk 상품 검색"
                            value={searchText}
                            onChangeText={onChangeSearchText}
                        />}
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            disabled={
                                loadingAny
                            }
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={BulkProductsDictionary.tableProps.tableId}
                tableProps={BulkProductsDictionary.tableProps}
                records={products}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onChangeCountToDisplay={setCountToDisplay}
            />
            <PromptDialog
                open={editorState.open}
                title={editorState.item ? 'Bulk 상품명 수정' : 'Bulk 상품 등록'}
                message={"한글 16자 이내로 입력하세요."}
                defaultValue={editorState.item ? editorState.item.prodName : ''}
                isEdit={editorState.item ? true : false}
                onSubmit={onSubmitProdName}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
            />
            <TableDialog
                open={tableDialogData.open}
                tableName={tableDialogData.param.tableName}
                title={tableDialogData.param.title}
                startIcon={<BusinessIcon size={AppWord.LARGE}/>}
                records={tableDialogData.param.records}
                tableProps={tableDialogData.param.tableProps}
                onClose={()=>setTableDialogData({open:false, param:{}})}
            />
        </ContentConainer>
    );
}
